import { useState, useCallback, useRef } from 'react';
import { appointmentsApi } from '../../../services/bookingApi';
import { Appointment, CreateAppointmentPayload, UpdateAppointmentPayload } from '../types';
import { useNotification } from '../../../contexts/NotificationContext';
import { wallClockToISO } from '../../BookingConfiguration/utils';

// ─── Helpers ───────────────────────────────────────────────────────────────

const getErrorMessage = (err: any, fallback: string): string => {
  const data = err.response?.data;
  if (data?.error?.message) return data.error.message;
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail) && data.detail.length > 0) {
    const first = data.detail[0];
    return `${first.loc ? first.loc.join('.') + ': ' : ''}${first.msg}` || fallback;
  }
  return data?.message || err.message || fallback;
};

// ─── Hook ──────────────────────────────────────────────────────────────────

export const useAppointments = (smbId: string, timezone?: string) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  // Pagination (agenda view)
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  /**
   * Canonical current range — always reflects what the calendar is showing.
   * Written by CalendarView's fetch effect; read by mutations for targeted refetch.
   */
  const currentRange = useRef<{
    mode: 'timed' | 'agenda';
    start: Date;
    end: Date;
    page?: number;
    limit?: number;
  } | null>(null);

  /**
   * Active fetch abort controller.
   * Each new fetch cancels the previous one to eliminate race conditions.
   */
  const abortRef = useRef<AbortController | null>(null);

  // ── fetchAppointments (week / day / month) ─────────────────────────────

  /**
   * Fetch appointments for timed views (week, day, month).
   * Cancels any in-flight request before starting a new one.
   */
  const fetchAppointments = useCallback(async (start: Date, end: Date) => {
    if (!smbId) return;

    // Record what the calendar is currently showing
    currentRange.current = { mode: 'timed', start, end };

    // Cancel previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, any> = {
        smb_id: smbId,
        status: 'ACTIVE',
        min_start_time: wallClockToISO(start, timezone),
        max_end_time: wallClockToISO(end, timezone),
      };

      const data = await appointmentsApi.getAppointments(params);

      // Request was superseded — discard result
      if (controller.signal.aborted) return;

      setAppointments(data.appointments ?? []);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('[useAppointments] fetchAppointments failed', err);
      setError('Failed to fetch appointments.');
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [smbId, timezone]);

  // ── fetchAgendaAppointments ────────────────────────────────────────────

  /**
   * Fetch appointments for agenda view with server-side pagination.
   */
  const fetchAgendaAppointments = useCallback(async (
    start: Date,
    end: Date,
    currentPage: number = 1,
    currentLimit: number = 10,
  ) => {
    if (!smbId) return;

    currentRange.current = { mode: 'agenda', start, end, page: currentPage, limit: currentLimit };

    // Cancel previous request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, any> = {
        smb_id: smbId,
        status: 'ACTIVE',
        limit: currentLimit,
        page: currentPage,
        min_start_time: wallClockToISO(start, timezone),
        max_end_time: wallClockToISO(end, timezone),
      };

      const data = await appointmentsApi.getAppointments(params);

      if (controller.signal.aborted) return;

      setAppointments(data.appointments ?? []);
      setTotal(data.total ?? 0);
      setPage(currentPage);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('[useAppointments] fetchAgendaAppointments failed', err);
      setError('Failed to fetch appointments.');
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [smbId, timezone]);

  // ── refetchCurrentRange (used by mutations) ────────────────────────────

  /**
   * Re-fetch using the last recorded range.
   * Always uses the canonical currentRange — never stale.
   */
  const refetchCurrentRange = useCallback(async () => {
    const r = currentRange.current;
    if (!r) return;
    if (r.mode === 'agenda') {
      await fetchAgendaAppointments(r.start, r.end, r.page ?? 1, r.limit ?? 10);
    } else {
      await fetchAppointments(r.start, r.end);
    }
  }, [fetchAppointments, fetchAgendaAppointments]);

  // ── createAppointment ──────────────────────────────────────────────────

  const createAppointment = async (payload: CreateAppointmentPayload): Promise<boolean> => {
    setIsLoading(true);

    // Sanitize payload: strip empty spaces
    const sanitizedPayload = { ...payload };
    if (typeof sanitizedPayload.notes === 'string') {
      sanitizedPayload.notes = sanitizedPayload.notes.trim();
    }
    if (Array.isArray(sanitizedPayload.services)) {
      sanitizedPayload.services = sanitizedPayload.services
        .map(s => (typeof s === 'string' ? s.trim() : s))
        .filter(s => s.length > 0);
    }

    try {
      const created = await appointmentsApi.createAppointment(sanitizedPayload);
      showNotification('Appointment created successfully', 'success');

      if (created?.id) {
        // Optimistic insert for timed views — instant, no extra network round-trip.
        // For agenda: always refetch because server controls pagination order.
        if (currentRange.current?.mode === 'agenda') {
          await refetchCurrentRange();
        } else {
          setAppointments(prev => [...prev, created]);
        }
      } else {
        // Server returned no body — fallback to refetch
        await refetchCurrentRange();
      }
      return true;
    } catch (err: any) {
      console.error('[useAppointments] createAppointment failed', err);
      showNotification(getErrorMessage(err, 'Failed to create appointment'), 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ── updateAppointment ──────────────────────────────────────────────────

  const updateAppointment = async (id: string, payload: UpdateAppointmentPayload): Promise<boolean> => {
    setIsLoading(true);
    try {
      const updated = await appointmentsApi.updateAppointment(id, payload);
      showNotification('Appointment updated successfully', 'success');

      if (updated?.id) {
        // Optimistic patch for timed views
        if (currentRange.current?.mode === 'agenda') {
          await refetchCurrentRange();
        } else {
          setAppointments(prev =>
            prev.map(apt => (apt.id === id ? { ...apt, ...updated } : apt))
          );
        }
      } else {
        // Server returned no body — fallback to refetch
        await refetchCurrentRange();
      }
      return true;
    } catch (err: any) {
      console.error('[useAppointments] updateAppointment failed', err);
      showNotification(getErrorMessage(err, 'Failed to update appointment'), 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    appointments,
    isLoading,
    error,
    fetchAppointments,
    fetchAgendaAppointments,
    createAppointment,
    updateAppointment,
    refetchCurrentRange,
    page,
    limit,
    total,
    setPage,
  };
};
